import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Avatar, Button, Chip, Paper, Popover, Stack, Tooltip, Typography } from '@mui/material';

import type { ProjectAssignee, ProjectTask } from '~/services/ContractingService';

import TaskAssigneePicker from './TaskAssigneePicker';
import { formatProjectDate } from './projectManagementModel';

interface Props {
  suggestedAssignees: Array<ProjectAssignee>;
  task: ProjectTask;
  onChangeAssignees: (taskId: string, assignees: Array<ProjectAssignee>) => void;
}

export default function TaskCard({ suggestedAssignees, task, onChangeAssignees }: Props) {
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [firstAssignee, ...otherAssignees] = task.assignees;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="body2" fontWeight={600}>
        {task.name}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {formatProjectDate(task.startDate)} - {formatProjectDate(task.endDate)}
      </Typography>

      <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 1 }}>
        {firstAssignee && (
          <Chip
            avatar={<Avatar>{firstAssignee.name.charAt(0)}</Avatar>}
            label={firstAssignee.name}
            size="small"
            sx={{ minWidth: 0 }}
            onClick={(event) => setPickerAnchor(event.currentTarget)}
          />
        )}
        {otherAssignees.length > 0 && (
          <Tooltip
            title={
              <Stack>
                {otherAssignees.map((assignee) => (
                  <Typography key={assignee.id} variant="body2">
                    {assignee.name}
                  </Typography>
                ))}
              </Stack>
            }
          >
            <Chip
              label={`+${otherAssignees.length}`}
              size="small"
              sx={{ flexShrink: 0 }}
              onClick={(event) => setPickerAnchor(event.currentTarget)}
            />
          </Tooltip>
        )}
        <Button
          color="inherit"
          endIcon={<ExpandMoreIcon />}
          size="small"
          sx={{ flexShrink: 0, ml: 'auto', textTransform: 'none' }}
          variant="text"
          onClick={(event) => setPickerAnchor(event.currentTarget)}
        >
          {task.assignees.length ? 'Edit' : 'Assign'}
        </Button>
      </Stack>

      <Popover
        anchorEl={pickerAnchor}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        slotProps={{ paper: { sx: { mt: 0.5 } } }}
        open={!!pickerAnchor}
        onClose={() => setPickerAnchor(null)}
      >
        <TaskAssigneePicker
          assignees={task.assignees}
          suggestedAssignees={suggestedAssignees}
          onChange={(assignees) => onChangeAssignees(task.id, assignees)}
        />
      </Popover>
    </Paper>
  );
}
